import { useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import Toast from 'react-native-toast-message';
import * as Crypto from 'expo-crypto';
import SheetModal from '@/components/General/SheetModal';
import Input from '@/components/General/inputs/Input';
import InputSection from '@/components/General/inputs/InputSection';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import CareDetailsFields from '@/components/FollowUp/Care/CareDetailsFields';
import { Query } from '@/utils/react-query/Queries';
import { Mutation } from '@/utils/react-query/Mutations';
import { buildCareDetails, toMealDateKey } from '@/utils/followUpCare';

const EMPTY_DETAILS = {
    location: '',
    locationInstructions: '',
    visitPreferences: '',
    coordinationContact: '',
    deliveryInstructions: '',
    reviewDate: null,
};

/**
 * StartCareSheet — template-free care intake (§25.9). The form asks for
 * only what the episode needs: the person (prefilled from a person
 * surface; picked here when opened from the Overview), an optional short
 * context, an optional Notify list, and optional care details behind
 * progressive disclosure.
 *
 * No Path picker, type selection, step editor, deadline, quota, mandatory
 * connector, or meal setup. There is one ACTIVE episode per person — a
 * repeated start returns the same episode (alreadyOpen) and NEVER re-sends
 * start pushes. Notify selection creates no ownership and no saved
 * recipient list; no recipients / no devices never blocks or warns —
 * dashboard visibility is the agreed fallback.
 */
const StartCareSheet = ({ isOpen, onClose, organizationID, personID, personName }) => {
    const router = useRouter();
    const idempotencyKeyRef = useRef(null);
    const notifyEditedRef = useRef(false);

    const [person, setPerson] = useState(null);
    const [context, setContext] = useState('');
    const [notify, setNotify] = useState([]);
    const [details, setDetails] = useState(EMPTY_DETAILS);
    const [detailsOpen, setDetailsOpen] = useState(false);

    const effectivePersonID = personID || person?.id || null;

    // The person's primary connector preselects into Notify (§25.9) — a
    // transient selection, never an ownership change. Resolved through the
    // team care surface so the same query serves person and picker cases.
    const { data: followUpPerson } = Query.followUp.person({
        organizationID,
        personID: effectivePersonID,
        options: { enabled: isOpen && !!organizationID && !!effectivePersonID },
    });
    const connectorID = followUpPerson?.primaryCaregiverPersonID || null;
    const { data: connector } = Query.people.basicById({
        id: connectorID,
        options: { enabled: isOpen && !!connectorID },
    });

    const startMutation = Mutation.followUp.startCare({
        onSuccess: (data) => {
            const careEpisodeID = data?.careEpisodeID;
            Toast.show({
                type: data?.alreadyOpen ? 'info' : (data?.needsOwner ? 'info' : 'success'),
                text2: data?.alreadyOpen
                    ? 'Care is already active for this person. Opening the episode.'
                    : data?.needsOwner
                        ? 'Care started. This person still needs a primary connector.'
                        : 'Care started',
            });
            onClose?.();
            if (careEpisodeID) {
                router.push(`/follow-up/care/${careEpisodeID}`);
            }
        },
        onError: (error) => {
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to start care. Please try again.' });
            }
        },
    });

    // Fresh idempotency key + form reset per open session — a retried submit
    // replays server-side instead of duplicating the episode or its pushes.
    useEffect(() => {
        if (isOpen) {
            idempotencyKeyRef.current = Crypto.randomUUID();
            notifyEditedRef.current = false;
            setPerson(null);
            setContext('');
            setNotify([]);
            setDetails({ ...EMPTY_DETAILS });
            setDetailsOpen(false);
        } else {
            idempotencyKeyRef.current = null;
        }
    }, [isOpen]);

    // Late caregiver data hydrates the preselection — never overrides a
    // deliberate user edit (removing the connector must stick).
    useEffect(() => {
        if (!isOpen || notifyEditedRef.current || !connectorID) return;
        setNotify((current) => {
            if (current.some((p) => p?.id === connectorID)) return current;
            return [...current, connector || { id: connectorID }];
        });
    }, [isOpen, connectorID, connector]);

    const notifyIds = useMemo(
        () => (notify || []).map((p) => p?.id).filter(Boolean),
        [notify]
    );

    const handleSubmit = () => {
        if (!effectivePersonID || startMutation.isPending) return;
        const careDetails = buildCareDetails({
            ...details,
            reviewDate: details.reviewDate ? toMealDateKey(details.reviewDate) : null,
        });
        startMutation.mutate({
            organizationID,
            personID: effectivePersonID,
            payload: {
                personID: effectivePersonID,
                idempotencyKey: idempotencyKeyRef.current,
                ...(context.trim() ? { context: context.trim() } : {}),
                ...(careDetails ? { careDetails } : {}),
                ...(notifyIds.length ? { notifyPersonIDs: notifyIds } : {}),
            },
        });
    };

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            onCancel={onClose}
            title="Start Care"
            subtitle={personName || person ? `for ${personName || `${person?.firstName || ''} ${person?.lastName || ''}`.trim()}` : undefined}
            stayOpenOnBlur
            snapPoints={[520, 720]}
            cancelText="Cancel"
            confirmText="Start Care"
            onConfirm={effectivePersonID && !startMutation.isPending ? handleSubmit : null}
            loading={startMutation.isPending}
        >
            <ScrollView style={styles.scroll} keyboardShouldPersistTaps="handled">
                {!personID && (
                    <SheetSelector
                        type="people"
                        title="Person"
                        placeholder="Who needs care?"
                        value={person}
                        onChange={setPerson}
                        required
                        testID="start-care-person-picker"
                    />
                )}

                <Input
                    title="What's going on? (optional)"
                    placeholder="e.g. Hospital stay, Home recovery"
                    value={context}
                    maxLength={120}
                    onUpdate={setContext}
                    testID="start-care-context"
                />

                <SheetSelector
                    type="people"
                    title="Notify people (optional)"
                    placeholder="Choose people to push a notification to"
                    multiple
                    value={notify}
                    onChange={(value) => {
                        notifyEditedRef.current = true;
                        setNotify(Array.isArray(value) ? value : []);
                    }}
                    excludeIds={effectivePersonID ? [effectivePersonID] : []}
                    testID="start-care-notify-picker"
                />
                <Text style={styles.hint}>
                    They get a push notification once. This doesn&apos;t assign them or save a list — active care stays visible on the dashboard either way.
                </Text>

                {!detailsOpen ? (
                    <InputSection>
                        <Text
                            testID="start-care-details-toggle"
                            accessibilityRole="button"
                            style={styles.detailsToggle}
                            onPress={() => setDetailsOpen(true)}
                        >
                            Add care details (optional)
                        </Text>
                    </InputSection>
                ) : (
                    <InputSection title="Care details" description="All optional. The episode starts either way.">
                        <CareDetailsFields
                            values={details}
                            onFieldChange={(key, value) => setDetails((d) => ({ ...d, [key]: value }))}
                            testIDPrefix="start-care"
                            disabled={startMutation.isPending}
                        />
                    </InputSection>
                )}
                <View style={styles.bottomPad} />
            </ScrollView>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    scroll: { flexGrow: 0 },
    hint: {
        fontSize: 12,
        color: colors.textSecondary,
        fontStyle: 'italic',
        marginTop: 4,
        paddingHorizontal: 4,
    },
    detailsToggle: {
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.paths,
        paddingVertical: 8,
        _web: { cursor: 'pointer' },
    },
    bottomPad: { height: 16 },
}));

export default StartCareSheet;
